import React, { useEffect, useMemo, useRef, useState } from "react";

import { FaChevronDown, FaSearch } from "react-icons/fa";

import "./filtro-multiplo.css";

/* Filtro de seleção múltipla com "Todos", no desenho dos filtros do
   Avaliando Conhecimentos: um cartão com o rótulo e o resumo da escolha, que
   abre uma lista de caixas de seleção.

   `selecionados` vazio é "Todos". Escolher um item a partir de "Todos" fica só
   com ele; desmarcar o último volta para "Todos". Com `semTodos` (Perfil) não
   há essa opção, e pelo menos um item fica marcado. */

// Nomes do banco chegam com entidades HTML ("COL&Eacute;GIO")
const decodificador =
  typeof document !== "undefined" ? document.createElement("textarea") : null;
export const decodificarNome = nome => {
  if (!decodificador || !nome || !String(nome).includes("&")) return nome;
  decodificador.innerHTML = nome;
  return decodificador.value;
};

const BUSCA_A_PARTIR_DE = 8;

export default function FiltroMultiplo({
  rotulo,
  opcoes = [],
  selecionados = [],
  onChange,
  disabled = false,
  carregando = false,
  semTodos = false,
  dica
}) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");
  const raiz = useRef(null);
  const campoBusca = useRef(null);

  useEffect(() => {
    if (!aberto) return undefined;
    const fecharFora = evento => {
      if (raiz.current && !raiz.current.contains(evento.target))
        setAberto(false);
    };
    const fecharEsc = evento => evento.key === "Escape" && setAberto(false);
    document.addEventListener("mousedown", fecharFora);
    document.addEventListener("keydown", fecharEsc);
    return () => {
      document.removeEventListener("mousedown", fecharFora);
      document.removeEventListener("keydown", fecharEsc);
    };
  }, [aberto]);

  useEffect(() => {
    if (!aberto) setBusca("");
    // Lista longa (escolas, turmas): quem abriu quer digitar o nome
    else campoBusca.current?.focus();
  }, [aberto]);

  const visiveis = useMemo(() => {
    const termo = busca
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .trim();
    if (!termo) return opcoes;
    return opcoes.filter(opcao =>
      String(decodificarNome(opcao.name) || "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .includes(termo)
    );
  }, [busca, opcoes]);

  const todos = !semTodos && selecionados.length === 0;

  const resumo = () => {
    if (carregando) return "Carregando...";
    if (todos) return "Todos";
    if (selecionados.length === 1) {
      const opcao = opcoes.find(item => item.id === selecionados[0]);
      return decodificarNome(opcao?.name) || "1 selecionado";
    }
    if (!selecionados.length) return "Nenhum";
    return `${selecionados.length} selecionados`;
  };

  const alternar = id => {
    if (selecionados.includes(id)) {
      const restantes = selecionados.filter(item => item !== id);
      if (semTodos && !restantes.length) return;
      onChange(restantes);
    } else {
      onChange([...selecionados, id]);
    }
  };

  const bloqueado = disabled || carregando;

  return (
    <div
      ref={raiz}
      className={`filtro-multiplo${aberto ? " is-open" : ""}${
        bloqueado ? " is-disabled" : ""
      }`}
    >
      <button
        type="button"
        className="filtro-multiplo__gatilho"
        onClick={() => !bloqueado && setAberto(valor => !valor)}
        disabled={bloqueado}
        aria-haspopup="listbox"
        aria-expanded={aberto}
        title={bloqueado && dica ? dica : undefined}
      >
        <span className="filtro-multiplo__rotulo">{rotulo}</span>
        <span className="filtro-multiplo__valor">{resumo()}</span>
        <FaChevronDown className="filtro-multiplo__seta" aria-hidden="true" />
      </button>

      {aberto && (
        <div
          className="filtro-multiplo__painel"
          role="listbox"
          aria-multiselectable
        >
          {opcoes.length >= BUSCA_A_PARTIR_DE && (
            <div className="filtro-multiplo__busca">
              <FaSearch aria-hidden="true" />
              <input
                ref={campoBusca}
                value={busca}
                placeholder="Pesquisar"
                aria-label={`Pesquisar ${rotulo}`}
                onChange={evento => setBusca(evento.target.value)}
              />
            </div>
          )}
          <ul>
            {!semTodos && !busca && (
              <li>
                <label
                  className={`filtro-multiplo__opcao${
                    todos ? " is-checked" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={todos}
                    onChange={() => onChange([])}
                  />
                  <span>Todos</span>
                </label>
              </li>
            )}
            {visiveis.map(opcao => {
              const marcado = selecionados.includes(opcao.id);
              return (
                <li key={opcao.id}>
                  <label
                    className={`filtro-multiplo__opcao${
                      marcado ? " is-checked" : ""
                    }`}
                    title={decodificarNome(opcao.name)}
                  >
                    <input
                      type="checkbox"
                      checked={marcado}
                      onChange={() => alternar(opcao.id)}
                    />
                    <span>{decodificarNome(opcao.name)}</span>
                    {opcao.detalhe && (
                      <small>{decodificarNome(opcao.detalhe)}</small>
                    )}
                  </label>
                </li>
              );
            })}
            {!visiveis.length && (
              <li className="filtro-multiplo__vazio">
                {busca ? "Nada encontrado." : "Nenhuma opção."}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
